import { format as formatDate } from "date-fns";
import React from "react";
import ReactTooltip from "react-tooltip";
import { t } from "ttag";

import { Trans } from "../../../common/Trans";
import { useAppSelector } from "../../reducers";
import { IRootState } from "../../reducers.interfaces";
import {
    PredictedDeliveryDateDisplayType,
    PredictedDeliveryDateStatus,
} from "../constants";
import { PredictedDeliveryLoader } from "./PredictedDeliveryLoader";

import styles from "./PredictedDeliveryHelpText.module.scss";

const shouldShowFallback = (
    type: PredictedDeliveryDateDisplayType,
    status: PredictedDeliveryDateStatus,
): boolean => {
    return (
        type === PredictedDeliveryDateDisplayType.DISABLED ||
        status === PredictedDeliveryDateStatus.INITIAL ||
        status === PredictedDeliveryDateStatus.ERROR ||
        status === PredictedDeliveryDateStatus.LOADING
    );
};

export const PredictedDeliveryHelpText = (props: {
    type: PredictedDeliveryDateDisplayType;
    additionalHelpText: string;
}): React.ReactNode => {
    const prediction = useAppSelector(
        (rootState: IRootState) =>
            rootState.checkout.data.predicted_delivery_date,
    );

    if (shouldShowFallback(props.type, prediction.status)) {
        return <span>{props.additionalHelpText}</span>;
    }

    // Type narrowing: if we passed shouldShowFallback, we know prediction has data
    if (prediction.status !== PredictedDeliveryDateStatus.LOADED) {
        return <span>{props.additionalHelpText}</span>;
    }

    const tipId = "predicted-delivery-date-help-text__tooltip";
    const displayDate =
        props.type === PredictedDeliveryDateDisplayType.SHOW_AGGRESSIVE
            ? prediction.data.date_aggressive
            : prediction.data.date_conservative;
    const postcode = prediction.data.postcode;

    return (
        <>
            <PredictedDeliveryLoader />
            <Trans
                fmtString={t`Arrives to <Location></Location> as soon as <Date></Date>. <HelpText></HelpText>`}
                data={{
                    Location: (_, idx) => (
                        <span
                            aria-describedby={tipId}
                            className={styles.zip}
                            data-for={tipId}
                            data-tip={true}
                            key={idx}
                            tabIndex={0}
                        >
                            {postcode}
                        </span>
                    ),
                    Date: (_, idx) => (
                        <span key={idx}>
                            {/* TODO(i18n): Date format "MMMM do" is English-specific and doesn't support localized date formatting. Consider using date-fns locale support or a localization-aware date formatter. */}
                            {formatDate(displayDate, "MMMM do")}
                        </span>
                    ),
                    HelpText: (_, idx) => (
                        <span key={idx}>{props.additionalHelpText}</span>
                    ),
                }}
            />
            <ReactTooltip
                aria-label={t`Question about your order?`}
                class={styles.tooltip}
                event={"mouseover focus"}
                eventOff="scroll mousewheel mouseleave mouseout blur"
                id={tipId}
                place="top"
                role="tooltip"
                isCapture={true}
            >
                {/* TODO(i18n): Wrap in t tagged template: {t`Need to change your delivery ZIP Code? Please return to the "Shipping" section to update your address.`} */}
                <span>
                    Need to change your delivery ZIP Code? Please return to the
                    &quot;Shipping&quot; section to update your address.
                </span>
            </ReactTooltip>
        </>
    );
};
